import { useState, useMemo } from 'react';
import { useSnackbar } from 'notistack';
import {
  Page,
  PageTitleWithButton,
  Table,
  Pagination,
  Loader,
  MobileTable,
  SearchSection
} from '../../../components';
import { useQuery } from 'react-query';
import { Box, Button, useMediaQuery } from '@mui/material';
import { Edit, Add, DeleteForever } from '@mui/icons-material';
import { formatInputPhoneNumber } from '../../../helpers';
import { getProjectManagers, deleteProjectManager } from '../../../fetch';
import { IResponse, IProjectManager } from '../../../models';
import { ProjectManagerModal } from './project-manager-modal';
import { ProjectManagerFilters } from './page-filters';

export const ProjectManagersPage = () => {
  const { enqueueSnackbar } = useSnackbar();
  const [isLoadingProjectManager, setLoadingProjectManger] = useState<boolean>(false);
  const [isModalOpen, showProjectManagerModal] = useState<boolean>(false);
  const [currentProjectManagerId, setCurrentProjectManagerId] = useState<string | number>(
    undefined
  );
  const [page, setPage] = useState<number>(0);
  const [recordCount, setRecordCount] = useState<number>(0);
  const [perPage, setRowsPerPage] = useState<number>(10);
  const [selectedSort, setSelectedSort] = useState<string>('Name');
  const [sortDirection, setSortDirection] = useState<{
    Name?: 'Asc' | 'Desc';
    DisplayName?: 'Asc' | 'Desc';
    ContactPhoneNumber?: 'Asc' | 'Desc';
    ActiveBuildCount?: 'Asc' | 'Desc';
  }>({
    Name: 'Asc'
  });
  const [hasAppliedFilters, setHasAppliedFilters] = useState<boolean>(false);
  const [filters, setFilters] = useState<{
    search?: string;
    neighborhoodId?: string | number;
    territoryId?: string | number;
  } | null>({});
  const [selectedNeighborhoodId, setSelectedNeighborhoodId] = useState<string | number>('');
  const [selectedTerritoryId, setSelectedTerritoryId] = useState<string | number>('');
  const [searchedValue, setSearchedValue] = useState<string>('');
  const [searchValue, setSearchValue] = useState<string>('');

  const isMobile = useMediaQuery('(max-width: 960px)');

  const {
    isLoading: isLoadingProjectManagers,
    data: projectMangersData,
    refetch: refetchProjectManagers
  } = useQuery<IResponse<IProjectManager[]>, Error>(
    ['getProjectManagers', page, perPage, selectedSort, sortDirection, filters, searchedValue],
    () =>
      getProjectManagers({
        page: page + 1,
        perPage,
        sortBy: selectedSort,
        sortDirection: sortDirection[selectedSort] as string,
        search: searchedValue || filters?.search,
        territoryId: filters?.territoryId,
        neighborhoodId: filters?.neighborhoodId
      }),
    {
      onSuccess: data => {
        setRecordCount(data.totalRecordCount);
      },
      notifyOnChangeProps: 'tracked'
    }
  );
  const projectManagers = useMemo(() => projectMangersData?.records ?? [], [projectMangersData]);

  const handleClickColumn = (column: string) => {
    setSelectedSort(column);
    setSortDirection({
      ...sortDirection,
      [column]: sortDirection[column] === 'Asc' ? 'Desc' : 'Asc'
    });
  };

  const handleEdit = async (id: string | number) => {
    setCurrentProjectManagerId(id);
    showProjectManagerModal(true);
  };
  const handleDelete = async (id: string | number) => {
    const result = window.confirm('Are you sure you want to delete this project manager?');

    if (result) {
      setLoadingProjectManger(true);
      try {
        await deleteProjectManager(id);
        refetchProjectManagers();
        enqueueSnackbar('Project manager removed!', {
          variant: 'success'
        });
      } catch (error) {
        enqueueSnackbar(error?.Detail ?? `Error deleting project manager, please try again.`, {
          variant: 'error'
        });
      } finally {
        setLoadingProjectManger(false);
      }
    }
  };
  const columns = useMemo(() => {
    return [
      {
        Header: 'Name',
        accessor: 'name',
        isServerSorted: selectedSort === 'Name',
        isServerSortedDesc: sortDirection.Name === 'Desc',
        handleClickColumn: () => handleClickColumn('Name')
      },
      {
        Header: 'Display Name',
        accessor: 'displayName',
        isServerSorted: selectedSort === 'DisplayName',
        isServerSortedDesc: sortDirection.DisplayName === 'Desc',
        handleClickColumn: () => handleClickColumn('DisplayName')
      },
      {
        Header: 'Territories',
        accessor: 'territories',
        sort: false,
        Cell: ({
          cell: {
            row: { original }
          }
        }: {
          cell: { row: { original: IProjectManager } };
        }) => {
          return <>{original.territories.join(', ')}</>;
        }
      },
      {
        Header: 'Neighborhoods',
        accessor: 'neighborhoods',
        sort: false,
        Cell: ({
          cell: {
            row: { original }
          }
        }: {
          cell: { row: { original: IProjectManager } };
        }) => {
          return <>{original.neighborhoods.join(', ')}</>;
        }
      },
      {
        Header: 'Contact Number',
        accessor: 'contactPhoneNumber',
        isServerSorted: selectedSort === 'ContactPhoneNumber',
        isServerSortedDesc: sortDirection.ContactPhoneNumber === 'Desc',
        handleClickColumn: () => handleClickColumn('ContactPhoneNumber'),
        Cell: ({
          cell: {
            row: { original }
          }
        }: {
          cell: { row: { original: IProjectManager } };
        }) => {
          return <>{formatInputPhoneNumber(original.contactPhoneNumber)}</>;
        }
      },
      {
        Header: 'Active Build Count',
        accessor: 'activeBuildCount',
        isServerSorted: selectedSort === 'ActiveBuildCount',
        isServerSortedDesc: sortDirection.ActiveBuildCount === 'Desc',
        handleClickColumn: () => handleClickColumn('ActiveBuildCount')
      },
      {
        id: 'actions',
        accessor: '',
        Header: '',
        overrideWidth: 200,
        sort: false,
        Cell: ({
          cell: {
            row: { original }
          }
        }: {
          cell: { row: { original: IProjectManager } };
        }) => {
          return (
            <Box display="flex">
              <Button
                id="edit"
                color="primary"
                startIcon={<Edit />}
                onClick={async () => {
                  handleEdit(original.projectManagerId);
                }}
              >
                Edit
              </Button>
              <Button
                id="delete"
                color="error"
                startIcon={<DeleteForever />}
                onClick={async () => {
                  handleDelete(original.projectManagerId);
                }}
              >
                Delete
              </Button>
            </Box>
          );
        }
      }
    ];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectManagers]);
  return (
    <>
      {isLoadingProjectManager && (
        <Loader position="centered" type="fullscreen">
          Loading...
        </Loader>
      )}
      <Page title="Project Managers">
        <PageTitleWithButton
          pageTitleProps={{
            title: 'Project Managers',
            showDivider: false
          }}
          button={
            <Button
              startIcon={<Add />}
              onClick={() => showProjectManagerModal(true)}
              color="primary"
              variant="contained"
            >
              Add New Project Manager
            </Button>
          }
        />
        <Box mt={1}>
          <ProjectManagerFilters
            isLoading={isLoadingProjectManagers}
            setHasAppliedFilters={setHasAppliedFilters}
            hasAppliedFilters={hasAppliedFilters}
            setSearchValue={setSearchValue}
            setSearchedValue={setSearchedValue}
            searchValue={searchValue}
            setSelectedNeighborhoodId={setSelectedNeighborhoodId}
            selectedNeighborhoodId={selectedNeighborhoodId}
            setSelectedTerritoryId={setSelectedTerritoryId}
            selectedTerritoryId={selectedTerritoryId}
            handleSearch={(val: string) => {
              setPage(0);
              setSearchedValue(val);
            }}
            applyFilters={(clearFilters?: boolean, clearSearch?: boolean) => {
              if (clearFilters) {
                setFilters({
                  search: '',
                  neighborhoodId: undefined,
                  territoryId: undefined
                });
              } else {
                setPage(0);
                setFilters({
                  ...filters,
                  search: searchValue,
                  neighborhoodId: selectedNeighborhoodId || undefined,
                  territoryId: selectedTerritoryId || undefined
                });
              }
            }}
          />
          {searchedValue && (
            <SearchSection
              searchedValue={searchedValue}
              handleClearSearch={() => {
                setSearchValue('');
                setSearchedValue('');
                setHasAppliedFilters(false);
                setPage(0);
              }}
            />
          )}
        </Box>
        <Table
          columns={columns}
          data={projectManagers}
          isLoading={isLoadingProjectManagers}
          hidePagination
          mobileProps={{
            handleEdit: (val: IProjectManager) => {
              handleEdit(val.projectManagerId);
            },
            handleDelete: (val: IProjectManager) => {
              handleDelete(val.projectManagerId);
            },
            fields: columns
              .filter(c => c.Header)
              .map(c => ({
                name: c.Header,
                accessor: c.accessor,
                Cell: c?.Cell
              }))
          }}
          ResponsiveComponent={isMobile ? MobileTable : undefined}
        />
        {!isLoadingProjectManagers && (
          <Pagination
            page={page}
            count={recordCount}
            rowsPerPage={perPage}
            setPage={setPage}
            setRowsPerPage={setRowsPerPage}
          />
        )}
        <ProjectManagerModal
          projectManagerId={currentProjectManagerId}
          open={isModalOpen}
          onClose={() => {
            showProjectManagerModal(false);
            setTimeout(() => {
              setCurrentProjectManagerId(undefined);
            }, 500);
          }}
          fetchProjectManagers={refetchProjectManagers}
        />
      </Page>
    </>
  );
};
